<script setup>
import {ref, watch, computed} from "vue"
import {useForm} from "../form/useForm";
import store from "../../store";
import helpers from "@/lib/helpers";
import FormCustomerSelect from "../form/form-customer-select.vue";
import FormCreateInfo from "../form/form-create-info.vue";
import TransactionRefundBadge from "./transaction-refund-badge.vue";

const props = defineProps({
  value: {
    type: Object,
    required: true
  },
  order: Object
})
const emits = defineEmits(['input', 'push', 'refund', 'submit'])

const apply_amt = ref(null)
const mode = ref('Details')
const record = ref()
watch(() => props.value, (v) => record.value = v, {immediate: true})

const transaction = computed(() => props.value)

const {form, changed, save, state} = useForm(
    ['txn_date', 'fkcustomer', 'notes', 'm_type', 'external_id'],
    null,
    record,
    null,
    (v) => emits('input', v)
)

const apply = () => {
  helpers.asyncAction(props.order._links.transaction_create_url, {
    data: {
      _apply: apply_amt.value,
      id: props.value.id
    }
  }).then(() => {
    emits('input')
    apply_amt.value = null
  })
}

</script>

<template>
  <div>
    <modal-portal to="top">
      <modal-portal to="bottom">
        <div class="text-right" v-if="mode === 'Details'">
          <b-btn size="sm" variant="outline-primary" @click="save" class="px-3">Save</b-btn>
        </div>

        <div class="d-flex my-1" v-else-if="mode === 'Apply' && !!order && can('transactions.apply')">
          <modal-row label="Apply Amount" class="mr-auto">
            <b-input-group size="sm" prepend="$">
              <form-currency v-model="apply_amt"/>
            </b-input-group>
          </modal-row>
          <div class="my-auto my-lg-0">
            <b-btn size="sm" variant="outline-primary" class="px-4" @click="apply">Apply
              <div class="d-none d-lg-inline"> to Order</div>
            </b-btn>
          </div>
        </div>

      </modal-portal>
      <div class="d-flex mb-0">
        <div class="mb-auto mr-auto">
          <text-copy class="h5 mb-0">{{ transaction.gli }}</text-copy>
          <div class="text-muted">{{ transaction.display_type }}</div>
        </div>

        <div>
          <div class="d-block text-right" v-if="transaction.amount_native">
            <b-badge class="pr-1" variant="warning">{{ transaction.currency }}</b-badge>
            <span class="h6">{{ transaction.amount_native | money(transaction.currency) }}</span>
          </div>
          <div class="d-block text-right">
            <span class="text-muted pl-2">Gross </span><span
              class="h6">{{ transaction.amount | money }}</span>
          </div>
          <div class="d-block text-right">
            <span class="text-muted pl-2">Available </span><span
              class="h6">{{ transaction.available | money }}</span>
          </div>
          <div class="d-block text-right">
            <transaction-refund-badge :transaction="transaction"/>
          </div>
        </div>

        <div class="my-auto">
          <b-dropdown variant="link" no-caret dropleft>
            <template #button-content>
              <font-awesome-icon icon="fa-solid fa-ellipsis-v" size="lg"/>
            </template>
            <b-dropdown-item-button @click="$emit('refund', transaction)" v-if="transaction.can_refund">Refund
            </b-dropdown-item-button>
            <b-dropdown-item-button
                @click="asyncAction(transaction._links.self_url.href, 'PUT', {confirm: 'Void transaction?', data: {task: 'void', void: !transaction.voided}}).then((v) => emits('input', v))">
              {{ transaction.voided ? 'Un-Void' : 'Void' }}
            </b-dropdown-item-button>
            <div class="dropdown-divider"></div>
            <b-dropdown-item :href="transaction._links.receipt_pdf_url.href" target="_blank">Receipt PDF
            </b-dropdown-item>
            <b-dropdown-item target="_blank"
                             :to="{name: 'report-run', params: {id: 28}, query: {batch: true, record: transaction.id, type: 'transactions'}}">
              Audit
            </b-dropdown-item>
            <b-dropdown-item-button v-if="can('transactions.api-data') && transaction._links.api_data_url"
                                    @click="$emit('submit'); $router.push({name: 'data', params: {url: transaction._links.api_data_url}})">
              API Data
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
      </div>
      <b-alert class="mt-2" show v-if="transaction.voided" variant="warning">TRANSACTION IS VOID</b-alert>
      <b-alert class="mt-2" show v-if="transaction.funds_not_avail" variant="danger">FUNDS NOT AVAILABLE</b-alert>

      <chip-selector class="mb-0 mt-2" :items="['Details', 'Apply']" v-model="mode"/>
    </modal-portal>

    <div v-if="mode === 'Details'">
      <modal-row :feedback="state.fkcustomer" label="Customer">
        <form-customer-select :state="state.fkcustomer" v-model="form.fkcustomer"/>
      </modal-row>
      <modal-row :feedback="state.notes" label="Notes">
        <b-textarea rows="3" :state="state.notes | state" v-model="form.notes"/>
      </modal-row>

      <modal-row v-if="can('transactions.tags.read')" label="Tags">
        <div class="mt-3">
          <tag v-model="transaction.tags" :url="transaction._links.tag_url.href" write="transactions.tags.write"/>
        </div>
      </modal-row>

      <form-divider/>

      <form-create-info :value="transaction" no-divider/>

      <modal-row v-if="transaction.parent" label="Parent Transaction">
        <b-link @click="$emit('push', transaction.parent._links.self_url.href)">{{ transaction.parent.gli }}
          ({{ transaction.parent.amount | money }})
        </b-link>
      </modal-row>

      <modal-row v-if="transaction.children && !!transaction.children.length" label="Related Transactions">
        <b-link class="d-block" v-for="child in transaction.children" :key="child.id"
                @click="$emit('push', child._links.self_url.href)">{{ child.gli }} ({{ child.amount | money }})
        </b-link>
      </modal-row>

      <modal-row v-if="!!transaction.exchange_rate" label="Exchange Rate">
        {{ transaction.exchange_rate }}
      </modal-row>

      <modal-row v-if="!!transaction.has_refund" label="Refunded Amount">
        {{ transaction.has_refund | money }}
      </modal-row>

      <form-divider/>

      <template v-if="transaction.type === 'C'">
        <form-row label="Txn Date" :feedback="state.txn_date">
          <b-form-datepicker v-model="form.txn_date" :state="state.txn_date | state" size="sm"/>
        </form-row>
      </template>

      <template v-if="transaction.type === 'M'">
        <form-row label="Txn Date" :feedback="state.txn_date">
          <b-form-datepicker v-model="form.txn_date" :state="state.txn_date | state" size="sm"/>
        </form-row>

        <form-row label="Sub Type" :feedback="state.m_type">
          <b-select :options="store.state.settings.defaults.transaction_manual_type" :state="state.m_type | state"
                    v-model="form.m_type"
                    size="sm"/>
        </form-row>
        <modal-row label="Check / Ref #" :feedback="state.external_id">
          <b-form-input :state="state.external_id | state " v-model="form.external_id"/>
        </modal-row>
      </template>

      <template v-if="['B','S'].includes(transaction.type)">
        <template v-if="transaction.type === 'B'">
          <modal-row is-text label="Processor">Braintree</modal-row>

          <modal-row is-text label="Transaction ID">
            <component :is="transaction.external_url ? 'b-link' : 'span'" :href="transaction.external_url"
                       target="_blank">{{ transaction.external_id }}
              <font-awesome-icon class="pl-2" :icon="['fas', 'arrow-up-right-from-square']"
                                 v-if="transaction.external_url"
              />
            </component>
          </modal-row>
        </template>

        <template v-else-if="transaction.type === 'S'">
          <modal-row is-text label="Processor">Shopify</modal-row>
          <modal-row is-text label="Gateway">{{ transaction.shop_gateway }}</modal-row>
        </template>

        <modal-row is-text label="Card Number">{{ transaction.cc_number }}
          <!-- TODO
          @if (($transaction->type == OMS\Type\Enum\TransactionType::BRAINTREE) && !$transaction->isRefund())
          <a href="javascript:$.showWindow('/transaction/create?create=BC&original={{ $transaction->id }}&order={{ $apply_order->id }}')"
             class="">
            Reuse Card
          </a>
          -->
        </modal-row>

        <modal-row is-text label="Card Type - Expire">
          {{ transaction.cc_type }} - {{ transaction.cc_exp_month }} / {{ transaction.cc_exp_year }}
        </modal-row>

        <modal-row is-text label="Status">
          {{ transaction.external_status }}
        </modal-row>

        <modal-row is-text label="Type">
          {{ transaction.external_type }}
        </modal-row>

        <modal-row is-text label="Address">
          <span class="text-pre-line">{{ transaction.payer_address_string }}</span> <!-- make sure stays one line -->
        </modal-row>

        <template v-if="transaction.type === 'B'">
          <modal-row is-text label="AVS Postal Response">
            {{ transaction.cc_AVSPostalCode_string }} ({{ transaction.cc_AVSPostalCode || '?' }})
          </modal-row>

          <modal-row is-text label="CVV2 Response">
            {{ transaction.cc_CVV2Code_string }} ({{ transaction.cc_CVV2Code || '?' }})
          </modal-row>

          <modal-row is-text label="AVS Street Response">
            {{ transaction.cc_AVSStreetCode_string }} ({{ transaction.cc_AVSStreetCode || '?' }})
          </modal-row>

          <modal-row is-text label="Reject Reason" v-if="!!transaction.cc_rejectReason">
            {{ transaction.cc_rejectReason }}
          </modal-row>
        </template>
        <template v-else-if="transaction.type === 'S'">
          <modal-row is-text label="Payer Name'">
            {{ transaction.payer_name }}
          </modal-row>
          <modal-row is-text label="Order ID">
            {{ transaction.order_id }}
          </modal-row>
        </template>

        <modal-row is-text label="Fee">
          {{ transaction.fee | money }}
        </modal-row>
      </template>

      <template v-else-if="transaction.type === 'E'">
        <modal-row is-text label="Transaction ID">
          {{ transaction.external_id }}
        </modal-row>
        <modal-row is-text label="Payer">
          {{ transaction.payer_name }}
        </modal-row>
      </template>

      <template v-else-if="transaction.type === 'P'">
        <modal-row is-text label="Processor">Paypal</modal-row>
        <modal-row is-text label="Paypal Memo" v-if="!!transaction.pp_memo">
          {{ transaction.pp_memo }}
        </modal-row>

        <modal-row is-text label="Transaction ID">
          <component :is="transaction.external_url ? 'b-link' : 'span'" :href="transaction.external_url"
                     target="_blank">{{ transaction.external_id }}
            <font-awesome-icon class="pl-2" :icon="['fas', 'arrow-up-right-from-square']"
                               v-if="transaction.external_url"
            />
          </component>
        </modal-row>

        <modal-row is-text label="Address">
          <span class="text-pre-line">{{ transaction.payer_address_string }}</span> <!-- make sure stays one line -->
        </modal-row>

        <modal-row is-text label="Paypal Email">
          {{ transaction.pp_email }}
        </modal-row>

        <modal-row is-text label="Payment Status">
          {{ transaction.external_status }} / {{ transaction.external_type }}
        </modal-row>

        <modal-row is-text label="Fee">
          {{ transaction.fee | money }}
        </modal-row>

        <modal-row is-text label="Tax Paid to eBay" v-if="!!transaction.pp_platform_fees">
          {{ transaction.pp_platform_fees | money }}
        </modal-row>

        <modal-row is-text label="Gross Amount">
          {{ transaction.pp_gross_amount | money }}
        </modal-row>

        <modal-row is-text label="Net Amount">
          {{ transaction.pp_net_amount | money }}
        </modal-row>
      </template>
    </div>

    <div v-else-if="mode === 'Apply'" class="d-flex">
      <div class="mx-auto">
        <b-table style="width: 250px" :items="transaction.assigns" head-variant="light" :fields="[
             {key: 'gli', label: 'GLI', sortable: false, tdClass: 'text-nowrap p-2 text-left'},
             {key: 'net_amount', label: 'Amount', sortable: false, thClass: 'text-right', tdClass: 'text-nowrap text-right align-middle p-2'},
      ]">
          <template #cell(gli)="{item}">
            <component target="_blank" :href="'/order/' + item.id"
                       :is="order && order.id === item.id ? 'div' : 'b-link'">{{ item.gli }}
            </component>
          </template>
          <template #cell(net_amount)="{item}">
            {{ item.net_amount | money }}
          </template>
        </b-table>
        <!-- a renter's flight balance it paid (Flight Log operators) -->
        <b-table v-if="transaction.operators && transaction.operators.length" style="width: 250px" :items="transaction.operators" head-variant="light" :fields="[
             {key: 'name', label: 'Flight rental', sortable: false, tdClass: 'p-2 text-left'},
             {key: 'net_amount', label: 'Amount', sortable: false, thClass: 'text-right', tdClass: 'text-nowrap text-right align-middle p-2'},
      ]">
          <template #cell(name)="{item}">
            <b-link target="_blank" href="/flights">{{ item.name }}</b-link>
          </template>
          <template #cell(net_amount)="{item}">
            {{ item.net_amount | money }}
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>